import { useEffect, useMemo, useState, type KeyboardEvent } from "react";
import { ArrowUpRight, Check, Eye } from "lucide-react";
import { cn } from "@/utils/helpers";
import ChatMarkdown from "@/app/routes/dashboard/components/ChatMarkdown";
import type { FieldReport, FieldReportLink } from "@/types/fieldReport.type";
import type { MarketSignal } from "@/types/marketSignal.type";
import { hostOfUrl } from "./evidenceDisplay";
import {
  calendarDaysUntil,
  computeHorizon,
  daysLabel,
  formatFullDate,
  formatShortDate,
  isSameDay,
  kindLabelSingular,
} from "./signalMeta";
import {
  filedItems,
  filedNote,
  filedTitle,
  isInauguralNote,
  normalizeTitle,
  parseReport,
  plainText,
  sectionItems,
  sectionKind,
  sectionTitle,
  splitLead,
  type ParsedReport,
  type ReportSection,
  type SectionKind,
} from "./reportMarkdown";
import KindGlyph from "./KindGlyph";
import SourceFavicon from "./SourceFavicon";

function modeLabel(mode: FieldReport["mode"]): string {
  return mode === "activity_steered" ? "Steered by the team's activity" : "General read";
}

/** The phone's report chip: "20 Aug · General read" / "20 Aug · Activity". */
function chipLabel(report: FieldReport): string {
  const mode = report.mode === "activity_steered" ? "Activity" : "General read";
  return `${formatShortDate(report.createdAt)} · ${mode}`;
}

function urlLinks(report: FieldReport): (FieldReportLink & { url: string })[] {
  return (report.links ?? []).filter(
    (l): l is FieldReportLink & { url: string } => l.kind === "url" && !!l.url,
  );
}

/** An activity window only says something when it did not start on the day the report was filed. */
function activitySinceOf(report: FieldReport): string | null {
  if (!report.activitySince) return null;
  return isSameDay(report.activitySince, report.createdAt) ? null : report.activitySince;
}

/** The signal a filed bullet names, matched on its title. Exact first, then a
 *  long-enough prefix either way, so a reporter trimming a title still links. */
function matchSignal(title: string, byTitle: Map<string, MarketSignal>): MarketSignal | null {
  const key = normalizeTitle(title);
  if (!key) return null;
  const exact = byTitle.get(key);
  if (exact) return exact;
  if (key.length < 16) return null;
  for (const [k, s] of byTitle) {
    if (k.startsWith(key) || key.startsWith(k)) return s;
  }
  return null;
}

/** Where a filed signal stands now, in the words of the page it lives on. */
function tileState(signal: MarketSignal): string {
  switch (signal.status) {
    case "pending":
      return "Waiting";
    case "active": {
      if (!signal.validUntil) return "Steering · until superseded";
      if (computeHorizon(signal).expired) return "Steering · window ended";
      const days = Math.max(0, calendarDaysUntil(signal.validUntil));
      return `Steering · ${days === 0 ? "ends today" : `${daysLabel(days)} left`}`;
    }
    case "expired":
      return "Expired";
    case "graduated":
      return "Graduated";
    case "superseded":
      return "Superseded";
    case "dismissed":
      return signal.acceptedAt ? "Dismissed" : "Declined";
    default:
      return signal.status;
  }
}

function filedLine(n: number): string {
  if (n === 0) return "No signals filed";
  return `Filed ${n} signal${n === 1 ? "" : "s"}`;
}

function indexNote(report: FieldReport, parsed: ParsedReport, latest: boolean): string {
  const parts: string[] = [];
  if (latest) parts.push("Latest.");
  const since = activitySinceOf(report);
  if (since) parts.push(`Reads activity since ${formatShortDate(since)}.`);
  const filed = filedItems(parsed).length;
  parts.push(filed === 0 ? "No signals filed." : `Filed ${filed} signal${filed === 1 ? "" : "s"}.`);
  return parts.join(" ");
}

/* ----------------------------------------------------------------- modules */

/** A module's label: the report's own heading, set small, with how many it holds. */
function ModuleLabel({ id, heading, count }: { id: string; heading: string; count: number }) {
  return (
    <h3 className="sig-brief__label-h" id={id} dir="auto">
      {heading}
      <span className="sig-brief__label-n">{count}</span>
    </h3>
  );
}

/** "What changed in the market": the first point is the lead story, the rest are stories. */
function StoriesModule({ items }: { items: string[] }) {
  const [lead, ...rest] = items.map(splitLead);
  if (!lead) return null;
  return (
    <>
      <div className="sig-brief__lead">
        {lead.lead ? (
          <h4 className="sig-brief__lead-head" dir="auto">
            {plainText(lead.lead)}
          </h4>
        ) : null}
        {lead.text ? (
          <div className="sig-brief__standfirst sig-md" dir="auto">
            <ChatMarkdown content={lead.text} />
          </div>
        ) : null}
      </div>
      {rest.map((story, i) => (
        <div key={i} className="sig-brief__story">
          {story.lead ? (
            <h4 className="sig-brief__story-head" dir="auto">
              {plainText(story.lead)}
            </h4>
          ) : null}
          {story.text ? (
            <div className="sig-brief__story-text sig-md" dir="auto">
              <ChatMarkdown content={story.text} />
            </div>
          ) : null}
        </div>
      ))}
    </>
  );
}

/** "What the team's activity says": the report's own voice on the team's work, in one panel. */
function DeskModule({ items }: { items: string[] }) {
  return (
    <div className="sig-brief__desk">
      {items.map(splitLead).map((point, i) => (
        <div key={i} className="sig-brief__desk-point">
          {point.lead ? (
            <span className="sig-brief__desk-label" dir="auto">
              {plainText(point.lead)}
            </span>
          ) : null}
          {point.text ? (
            <div className="sig-brief__desk-text sig-md" dir="auto">
              <ChatMarkdown content={point.text} />
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/** "Follow-ups on the last report": each row names the report it follows up. */
function FollowupsModule({
  items,
  previous,
  onOpenReport,
}: {
  items: string[];
  previous: FieldReport | null;
  onOpenReport: (report: FieldReport) => void;
}) {
  const points = items.map(splitLead);
  if (!previous && points.every((p) => isInauguralNote(`${p.lead ?? ""} ${p.text}`))) {
    return <p className="sig-brief__muted">Nothing to follow up: this is the first report.</p>;
  }
  return (
    <div className="sig-brief__rows">
      {points.map((point, i) => (
        <div key={i} className="sig-brief__followup">
          {previous ? (
            <button
              type="button"
              className="sig-brief__from"
              onClick={() => onOpenReport(previous)}
            >
              From {formatShortDate(previous.createdAt)}
            </button>
          ) : (
            <span className="sig-brief__from is-plain">First report</span>
          )}
          <div className="sig-brief__followup-body">
            {point.lead ? (
              <h4 className="sig-brief__followup-head" dir="auto">
                {plainText(point.lead)}
              </h4>
            ) : null}
            {point.text ? (
              <div className="sig-brief__followup-text sig-md" dir="auto">
                <ChatMarkdown content={point.text} />
              </div>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}

/** "Watching": the open threads the next run re-checks. */
function WatchModule({ items }: { items: string[] }) {
  return (
    <ul className="sig-brief__watch">
      {items.map((item, i) => (
        <li key={i} className="sig-brief__watch-row">
          <Eye aria-hidden="true" />
          <div className="sig-brief__watch-text sig-md" dir="auto">
            <ChatMarkdown content={item} />
          </div>
        </li>
      ))}
    </ul>
  );
}

/** "Signals filed": a tile per signal the run filed, opening it where it now lives. */
function FiledModule({
  items,
  byTitle,
  flashSignalId,
  onOpenSignal,
}: {
  items: string[];
  byTitle: Map<string, MarketSignal>;
  flashSignalId: string | null;
  onOpenSignal: (signal: MarketSignal) => void;
}) {
  return (
    <ul className="sig-brief__tiles">
      {items.map((item, i) => {
        const title = filedTitle(item) || plainText(item);
        const note = filedNote(item);
        const match = matchSignal(title, byTitle);
        if (!match) {
          return (
            <li key={i} className="sig-brief__tile is-plain">
              <span className="sig-brief__tile-title" dir="auto">
                {title}
              </span>
              {note ? (
                <span className="sig-brief__tile-note" dir="auto">
                  {note}
                </span>
              ) : null}
            </li>
          );
        }
        return (
          <li key={i}>
            <button
              type="button"
              id={`sig-filed-${match.id}`}
              className={cn("sig-brief__tile", flashSignalId === match.id && "is-flash")}
              onClick={() => onOpenSignal(match)}
            >
              <span className="sig-brief__tile-kind">
                <KindGlyph kind={match.kind} size={14} className="sig-glyph" />
                <span className="sig-kind-label">{kindLabelSingular(match.kind)}</span>
              </span>
              <span className="sig-brief__tile-title" dir="auto">
                {match.title}
              </span>
              {note ? (
                <span className="sig-brief__tile-note" dir="auto">
                  {note}
                </span>
              ) : null}
              <span className="sig-brief__tile-state">{tileState(match)}</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/** Any section the briefing has no module for: its heading, then its prose and points. */
function DefaultModule({ section }: { section: ReportSection }) {
  return (
    <>
      {section.blocks.map((block, i) =>
        block.kind === "prose" ? (
          <div key={i} className="sig-report__p sig-md" dir="auto">
            <ChatMarkdown content={block.markdown} />
          </div>
        ) : (
          <ul key={i} className="sig-points">
            {block.items.map((item, j) => (
              <li key={j} className="sig-point sig-md" dir="auto">
                <ChatMarkdown content={item} />
              </li>
            ))}
          </ul>
        ),
      )}
    </>
  );
}

/* ------------------------------------------------------------------ trail */

interface BriefSection {
  id: string;
  kind: SectionKind;
  section: ReportSection;
  items: string[];
}

/**
 * The field reports, as a briefing.
 *
 * A masthead across the full width (the report's own headline, then one line of
 * date, mode, sources and what it filed), then the report in MAIN and a sticky
 * RAIL beside it: a table of contents that marks the section in view, the filed
 * reports index, and the report's sources.
 *
 * The report's known sections each get their own module, in the order the
 * report wrote them: the market's stories with a lead story, the desk's read of
 * the team's activity in one panel, follow-ups that name the report they follow
 * up, a watchlist, and the filed signals as tiles that open each signal where
 * it now lives. Any other section keeps the plain prose module.
 *
 * On a narrow desk it is one column: the filed reports become a row of chips
 * under the masthead, the contents are dropped, and the sources close the report.
 *
 * `initialReportId` / `flashSignalId` open the trail on one report with one
 * filed tile highlighted (a signal's "From the field report" link); the parent
 * remounts the trail to ask again.
 */
export default function ReportsTrail({
  reports,
  signals,
  onOpenSignal,
  initialReportId = null,
  flashSignalId = null,
  reduced,
}: {
  reports: FieldReport[];
  signals: MarketSignal[];
  onOpenSignal: (signal: MarketSignal) => void;
  initialReportId?: string | null;
  flashSignalId?: string | null;
  reduced: boolean;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(
    initialReportId ?? reports[0]?.id ?? null,
  );
  const [inView, setInView] = useState<string | null>(null);

  const parsed = useMemo(() => {
    const map = new Map<string, ParsedReport>();
    for (const r of reports) map.set(r.id, parseReport(r.content));
    return map;
  }, [reports]);

  const byTitle = useMemo(() => {
    const map = new Map<string, MarketSignal>();
    for (const s of signals) {
      const key = normalizeTitle(s.title);
      if (key && !map.has(key)) map.set(key, s);
    }
    return map;
  }, [signals]);

  const selectedIndex = Math.max(
    0,
    reports.findIndex((r) => r.id === selectedId),
  );
  const selected = reports[selectedIndex] ?? null;
  const previous = reports[selectedIndex + 1] ?? null;
  const doc = selected ? (parsed.get(selected.id) ?? parseReport(selected.content)) : null;

  const sections: BriefSection[] = useMemo(
    () =>
      (doc?.sections ?? []).map((section, i) => ({
        id: `sig-brief-${selected?.id ?? "none"}-${i}`,
        kind: sectionKind(section.heading),
        section,
        items: sectionItems(section),
      })),
    [doc, selected?.id],
  );

  // The section in view, for the contents: the first one crossing the band
  // under the sticky bar.
  useEffect(() => {
    const els = sections
      .map((s) => document.getElementById(s.id))
      .filter((el): el is HTMLElement => !!el);
    if (els.length === 0) return;
    const visible = new Set<string>();
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) visible.add(e.target.id);
          else visible.delete(e.target.id);
        }
        const first = sections.find((s) => visible.has(s.id));
        if (first) setInView(first.id);
      },
      { rootMargin: "-96px 0px -55% 0px", threshold: 0 },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [sections]);

  const openReport = (report: FieldReport) => {
    setSelectedId(report.id);
    setInView(null);
    document.getElementById("sig-brief-top")?.scrollIntoView({
      behavior: reduced ? "auto" : "smooth",
      block: "start",
    });
  };

  const selectAt = (index: number) => {
    const next = reports[Math.max(0, Math.min(reports.length - 1, index))];
    if (!next) return;
    setSelectedId(next.id);
    setInView(null);
    document.getElementById(`sig-report-opt-${next.id}`)?.focus();
  };

  const onIndexKeyDown = (e: KeyboardEvent, index: number) => {
    if (e.key === "ArrowDown" || e.key === "ArrowRight") {
      e.preventDefault();
      selectAt(index + 1);
    } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
      e.preventDefault();
      selectAt(index - 1);
    } else if (e.key === "Home") {
      e.preventDefault();
      selectAt(0);
    } else if (e.key === "End") {
      e.preventDefault();
      selectAt(reports.length - 1);
    }
  };

  if (!selected || !doc) return null;

  const sources = urlLinks(selected);
  const since = activitySinceOf(selected);
  const filedCount = filedItems(doc).length;
  const meta = [
    formatFullDate(selected.createdAt),
    modeLabel(selected.mode),
    sources.length > 0 ? `${sources.length} ${sources.length === 1 ? "source" : "sources"}` : null,
    filedLine(filedCount),
    since ? `Activity since ${formatShortDate(since)}` : null,
  ].filter(Boolean);
  const contents = sections.filter((s) => s.section.heading);
  const current = inView ?? contents[0]?.id ?? null;

  const moduleFor = (s: BriefSection) => {
    switch (s.kind) {
      case "stories":
        return <StoriesModule items={s.items} />;
      case "desk":
        return <DeskModule items={s.items} />;
      case "followups":
        return <FollowupsModule items={s.items} previous={previous} onOpenReport={openReport} />;
      case "watch":
        return <WatchModule items={s.items} />;
      case "filed":
        return (
          <FiledModule
            items={s.items}
            byTitle={byTitle}
            flashSignalId={flashSignalId}
            onOpenSignal={onOpenSignal}
          />
        );
      default:
        return <DefaultModule section={s.section} />;
    }
  };

  return (
    <div className="sig-brief" id="sig-brief-top">
      <header className="sig-brief__mast">
        <h2 className="sig-brief__title" id="sig-report-title" dir="auto">
          {doc.title || formatFullDate(selected.createdAt)}
        </h2>
        <p className="sig-brief__meta">{meta.join(" · ")}</p>
      </header>

      <article className="sig-brief__main" aria-labelledby="sig-report-title" key={selected.id}>
        {sections.map((s) => (
          <section
            key={s.id}
            id={s.id}
            className={cn("sig-brief__section", `sig-brief__section--${s.kind}`)}
            aria-labelledby={s.section.heading ? `${s.id}-h` : undefined}
          >
            {s.section.heading ? (
              s.kind === "other" ? (
                <h3 className="sig-brief__other-h" id={`${s.id}-h`} dir="auto">
                  {s.section.heading}
                </h3>
              ) : (
                <ModuleLabel id={`${s.id}-h`} heading={sectionTitle(s.section.heading)} count={s.items.length} />
              )
            ) : null}
            {moduleFor(s)}
          </section>
        ))}
      </article>

      <aside className="sig-brief__rail" aria-label="About this report">
        {contents.length > 0 ? (
          <nav className="sig-brief__toc" aria-label="In this report">
            <p className="sig-reports__label">In this report</p>
            <ul>
              {contents.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    className={cn("sig-brief__toc-row", current === s.id && "is-on")}
                    aria-current={current === s.id ? "location" : undefined}
                    onClick={() => {
                      setInView(s.id);
                      document
                        .getElementById(s.id)
                        ?.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" });
                    }}
                  >
                    <span className="sig-brief__toc-name" dir="auto">
                      {sectionTitle(s.section.heading ?? "")}
                    </span>
                    <span className="sig-brief__toc-n">{s.items.length}</span>
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        ) : null}

        <nav className="sig-reports__index" aria-label="Filed reports">
          <p className="sig-reports__label">Filed reports · {reports.length}</p>
          <ul className="sig-reports__list">
            {reports.map((report, i) => {
              const on = report.id === selected.id;
              const rowDoc = parsed.get(report.id);
              return (
                <li key={report.id}>
                  <button
                    type="button"
                    id={`sig-report-opt-${report.id}`}
                    className={cn("sig-reports__item", on && "is-on")}
                    aria-current={on ? "true" : undefined}
                    tabIndex={on ? 0 : -1}
                    onClick={() => {
                      setSelectedId(report.id);
                      setInView(null);
                    }}
                    onKeyDown={(e) => onIndexKeyDown(e, i)}
                  >
                    {/* The phone shows each report as a chip: a check on the one being read. */}
                    <span className="sig-reports__chip">
                      {on ? <Check aria-hidden="true" /> : null}
                      {chipLabel(report)}
                      {on ? " · Reading" : null}
                    </span>
                    <span className="sig-reports__when">
                      <span className="sig-reports__date">{formatFullDate(report.createdAt)}</span>
                      {on ? (
                        <span className="sig-reports__reading">
                          <span className="sig-reports__square" aria-hidden="true" />
                          Reading
                        </span>
                      ) : null}
                    </span>
                    <span className="sig-reports__mode">{modeLabel(report.mode)}</span>
                    {rowDoc ? (
                      <span className="sig-reports__note">{indexNote(report, rowDoc, i === 0)}</span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {sources.length > 0 ? (
          <div className="sig-reports__sources">
            <p className="sig-reports__label">Sources in this report</p>
            <ul>
              {sources.map((link, i) => {
                const host = hostOfUrl(link.url) ?? link.url;
                return (
                  <li key={`${link.url}-${i}`}>
                    <a
                      className="sig-reports__source"
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <span className="sig-reports__source-host">
                        <SourceFavicon host={host} size={13} />
                        <span className="sig-reports__source-name">{host}</span>
                        <ArrowUpRight className="sig-reports__source-open" aria-hidden="true" />
                      </span>
                      {link.label && link.label !== host ? (
                        <span className="sig-reports__source-label" dir="auto">
                          {link.label}
                        </span>
                      ) : null}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </aside>
    </div>
  );
}
